import { useState } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import { FormResult } from '@components/Form/FormResult';
import { FormSubmit } from '@components/Form/FormSubmit';

import { contactUsMessages } from '@data/ui-messages/contact-us';
import { serverMessages } from '@data/ui-messages/server';
import { FormElement, FormField } from '@interfaces/form';
import { postFetchOptions } from '@utils/api';
import { emailRegex, getFormElement } from '@utils/form';

import styles from './ContactForm.module.css';

interface ContactFormProps {
	formFields: FormField[];
}

export const ContactForm: React.VFC<ContactFormProps> = ({ formFields }) => {
	const intl = useIntl();
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [waitingForServer, setWaitingForServer] = useState(false);
	const router = useRouter();
	const {
		register,
		handleSubmit,
		setError,
		reset,
		clearErrors,
		formState: { errors, isValid },
	} = useForm({
		mode: 'onBlur',
	});

	const renderFormField = (field: FormField) => {
		const FieldElement: FormElement = getFormElement(field.type);
		const validationOptions = { ...field.validation };

		if (validationOptions.pattern && field.type === 'email') {
			validationOptions.pattern.value = emailRegex;
		}

		return (
			<FieldElement
				key={field.name}
				{...register(field.name, validationOptions as RegisterOptions)}
				{...field}
				required={Boolean(validationOptions.required?.value)}
			/>
		);
	};

	const onSubmit = (data: any) => {
		setWaitingForServer(true);

		fetch('/api/contact-us', {
			...postFetchOptions,
			body: JSON.stringify(data),
		})
			.then((result) => {
				if (result.status === 200) {
					setSubmitSuccess(true);
					reset();
				} else if (result.status === 500) {
					setError('server', {
						type: 'server',
						message: intl.formatMessage(serverMessages.internalServerError),
					});
				}
			})
			.catch((error) => {
				setError('server', {
					type: 'server',
					message: error.messageId
						? intl.formatMessage(
								serverMessages[error.messageId as keyof typeof serverMessages]
						  )
						: error.message,
				});
			})
			.finally(() => {
				setWaitingForServer(false);
			});
	};

	if (typeof errors.server?.message === 'string') {
		return (
			<FormResult
				autoClose
				isOpen={Boolean(errors.server)}
				message={errors.server.message}
				onClose={() => clearErrors()}
			/>
		);
	}

	return (
		<section data-testid="ContactForm">
			{submitSuccess ? (
				<FormResult>
					<h1 className="fluid-font-t4-bold">
						{intl.formatMessage(contactUsMessages.submitSuccessTitle)}
					</h1>

					<p className="fluid-font-t4">
						{intl.formatMessage(contactUsMessages.submitSuccessDescription)}
					</p>
				</FormResult>
			) : (
				<form
					className={classNames(styles['contact-form'], 'stack-block')}
					noValidate
					onSubmit={handleSubmit(onSubmit)}
				>
					{formFields.map((field) => {
						return renderFormField({ ...field, errors });
					})}

					<input
						{...register('future')}
						autoComplete="off"
						hidden
						type="text"
					/>

					<input {...register('locale')} type="hidden" value={router.locale} />

					<div className={styles['submit-wrapper']}>
						<FormSubmit
							disabled={waitingForServer}
							isLoading={waitingForServer}
							isValid={isValid}
							label={intl.formatMessage(contactUsMessages.buttonLabel)}
						/>
					</div>
				</form>
			)}
		</section>
	);
};
